import React from 'react';
import showRecordBanner from 'screens/Applications&WorkOrders/Components/showRecordBanner';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import EditIcon from 'assets/edit_outlined.svg?react';
import DownloadIcon from 'assets/download_outlined_green.svg?react';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes/paths';
import MessageContent from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/components/MessageContent';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';

/**
 * Hard-coded rather than `text-green-50`/`60`: this palette's `green-50` is a
 * pale mint, so the Tailwind classes of those names are different colours.
 */
const PDF_IDLE_COLOR = '#14803C';

const DOWNLOAD_BUTTON_WIDTH_PX = 150;
const PDF_LOADING_TEXT_COLOR = '#19A04B';

const BUTTON_STYLE: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  justifyContent: 'center'
};

const ICON_SIZE_PX = 18;

/**
 * Inline, not Tailwind: the kit's `button > span { display: block }` outranks a
 * single utility class, so `flex` never applied.
 */
const BUTTON_LABEL_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap'
};

interface DrawerFooterProps {
  recordId: string | null;
  workOrderId?: string;
  applicationCropSeasonIds?: string[];
  onClose: () => void;
  onDownloadPdf?: () => void;
  isDownloadingPdf?: boolean;
}

const DrawerFooter = ({
  recordId,
  workOrderId,
  applicationCropSeasonIds,
  onClose,
  onDownloadPdf,
  isDownloadingPdf = false
}: DrawerFooterProps) => {
  const history = useHistory();
  const {
    selectedCropSeasonIds,
    hasAllSelectedCropSeasonIds,
    setSelectedCropSeasonIds
  } = useSelectedCropSeasonIds();

  const handleEdit = () => {
    if (!recordId) return;
    history.push(`${paths.applications}/${recordId}/edit`);
    onClose();
  };

  const handleGoToApplication = () => {
    const previousCropSeasonIds = selectedCropSeasonIds;
    const needsBreadcrumbUpdate =
      !!applicationCropSeasonIds?.length &&
      !hasAllSelectedCropSeasonIds(applicationCropSeasonIds);

    if (needsBreadcrumbUpdate) {
      setSelectedCropSeasonIds(applicationCropSeasonIds as string[]);
    }

    // Must sit on the location descriptor; a second argument nests it as
    // `location.state.state`, which SummaryParamsContext can't read.
    history.push({
      pathname: paths.applications,
      state: { operationId: recordId }
    });

    onClose();

    const onReturn = () => {
      if (needsBreadcrumbUpdate) {
        setSelectedCropSeasonIds(previousCropSeasonIds);
      }
      history.push({
        pathname: paths.workOrders,
        state: { operationId: workOrderId }
      });
    };

    showRecordBanner({
      key: RECORD_SOURCE_MESSAGE_KEY,
      className: 'ant-message-dark-mode br-4 record-banner-anchored',
      content: (
        <MessageContent
          label="Viewing linked record."
          returnLabel="Return to Work Order"
          onReturn={onReturn}
        />
      ),
      duration: 0,
      icon: <></>
    });
  };

  return (
    <div
      data-testid="linked-record-drawer-footer"
      className="flex justify-between items-center border-t border-neutral-20 px-6 py-5"
    >
      <Button
        type={ButtonType.outline}
        onClick={onClose}
        size="large"
        data-testid="btn-close"
        style={BUTTON_STYLE}
      >
        Close
      </Button>
      <div className="flex gap-2">
        <Button
          type={ButtonType.outline}
          onClick={handleEdit}
          size="large"
          disabled={!recordId}
          data-testid="btn-edit"
          style={BUTTON_STYLE}
        >
          <span style={BUTTON_LABEL_STYLE}>
            <EditIcon
              width={ICON_SIZE_PX}
              height={ICON_SIZE_PX}
              className="shrink-0"
            />
            Edit
          </span>
        </Button>
        <Button
          type={ButtonType.outline}
          onClick={isDownloadingPdf ? undefined : onDownloadPdf}
          size="large"
          disabled={!recordId}
          aria-busy={isDownloadingPdf}
          aria-disabled={isDownloadingPdf}
          data-testid="btn-download-pdf"
          style={{
            ...BUTTON_STYLE,
            color: PDF_IDLE_COLOR,
            borderColor: PDF_IDLE_COLOR,
            width: DOWNLOAD_BUTTON_WIDTH_PX
          }}
        >
          {/* The house spinner (green arc) plus a "Loading..." label — antd's
              `loading` prop would render its own default spinner instead, which
              is what diverged. The wrapper sets the colour and type ramp the
              design specifies; the label inherits both. */}
          {isDownloadingPdf ? (
            <span style={{ color: PDF_LOADING_TEXT_COLOR }}>
              <LoadingSpinner inline size={ICON_SIZE_PX} message="Loading..." />
            </span>
          ) : (
            <span style={BUTTON_LABEL_STYLE}>
              <DownloadIcon
                width={ICON_SIZE_PX}
                height={ICON_SIZE_PX}
                className="shrink-0"
              />
              Download PDF
            </span>
          )}
        </Button>
        <Button
          type={ButtonType.primary}
          onClick={handleGoToApplication}
          size="large"
          disabled={!recordId}
          data-testid="btn-go-to-application"
          style={BUTTON_STYLE}
        >
          {/* open_in_new is the only one of the three drawn with currentColor,
              so it picks up the primary button's white on its own. */}
          <span style={BUTTON_LABEL_STYLE}>
            <OpenInNewIcon
              width={ICON_SIZE_PX}
              height={ICON_SIZE_PX}
              className="shrink-0"
            />
            Go to Application
          </span>
        </Button>
      </div>
    </div>
  );
};

export default DrawerFooter;
